import { z } from "zod";

export const meetupFoodIntents = [
  "local_pub", "first_round", "group", "korean_pub", "pocha", "old_school", "izakaya",
  "meat", "gopchang", "seafood", "spicy", "soup", "chinese", "japanese", "fried_chicken", "pizza",
] as const;
export const meetupFoodIntentSchema = z.enum(meetupFoodIntents);

export const meetupAreas = [
  "gangnam", "hongdae", "euljiro", "seongsu", "konkuk", "seomyeon",
  "haeundae", "dongseongro", "dunsan", "sangmu", "jeju_city",
] as const;
export const meetupAreaSchema = z.enum(meetupAreas);
export type MeetupArea = z.infer<typeof meetupAreaSchema>;

export const meetupAreaMeta: Record<MeetupArea, { label: string; region: string }> = {
  gangnam:{ label:"강남역", region:"서울" }, hongdae:{ label:"홍대입구", region:"서울" },
  euljiro:{ label:"을지로3가", region:"서울" }, seongsu:{ label:"성수", region:"서울" },
  konkuk:{ label:"건대입구", region:"서울" }, seomyeon:{ label:"서면", region:"부산" },
  haeundae:{ label:"해운대", region:"부산" }, dongseongro:{ label:"동성로", region:"대구" },
  dunsan:{ label:"둔산", region:"대전" }, sangmu:{ label:"상무", region:"광주" },
  jeju_city:{ label:"제주시청", region:"제주" },
};

export const meetupBudgetSchema = z.enum(["under_10k", "10_20k", "20_40k", "over_40k", "decide_together"]);
export const meetupAlcoholStyleSchema = z.enum(["meal_only", "light_optional", "food_first_non_drinkers_welcome"]);
export const meetupJoinModeSchema = z.enum(["instant", "approval"]);
export const meetupStatusSchema = z.enum(["recruiting", "full", "active", "ended", "chat_closed", "archived", "cancelled"]);
export const meetupParticipantStatusSchema = z.enum(["host", "pending", "accepted", "checked_in", "left", "rejected", "removed"]);

const safeTitle = z.string().trim().min(4).max(50);
const safeAlias = z.string().trim().min(2).max(16);

export const meetupListQuerySchema = z.object({
  area: meetupAreaSchema,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  intent: meetupFoodIntentSchema.optional(),
}).strict();

export const createMeetupSchema = z.object({
  title: safeTitle,
  area: meetupAreaSchema,
  foodIntent: meetupFoodIntentSchema,
  startAt: z.string().datetime(),
  plannedEndAt: z.string().datetime(),
  maxParticipants: z.number().int().min(3).max(8),
  budget: meetupBudgetSchema,
  alcoholStyle: meetupAlcoholStyleSchema,
  joinMode: meetupJoinModeSchema.default("instant"),
  venueName: z.string().trim().min(2).max(80),
  venueUrl: z.string().url().max(500).optional(),
  note: z.string().trim().max(160).default(""),
}).strict();

export const meetupSessionSchema = z.object({
  channel: z.enum(["web", "ait", "android"]),
  assertion: z.string().min(16).max(4_096),
  publicAlias: safeAlias,
}).strict();

export const meetupTestSessionSchema = z.object({
  subject: z.string().min(3).max(80),
  publicAlias: safeAlias,
}).strict();

export const meetupJoinSchema = z.object({
  answer: z.string().trim().max(80).default(""),
  expectedRevision: z.number().int().nonnegative(),
  idempotencyKey: z.string().uuid(),
}).strict();

export const meetupDecisionSchema = z.object({
  subjectId: z.string().min(16).max(128),
  expectedRevision: z.number().int().nonnegative(),
  idempotencyKey: z.string().uuid(),
}).strict();

export const meetupActionSchema = z.object({
  expectedRevision: z.number().int().nonnegative(),
  idempotencyKey: z.string().uuid(),
  checkInCode: z.string().regex(/^\d{4}$/).optional(),
}).strict();

export const meetupQuickMessages = [
  "10분 늦어요", "입구에 있어요", "먼저 주문해 주세요", "곧 도착해요", "안전하게 귀가했어요",
] as const;
export const meetupQuickMessageSchema = z.enum(meetupQuickMessages);
export const meetupMessageInputSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(["text", "quick"]),
  body: z.string().trim().min(1).max(500),
  expectedRevision: z.number().int().nonnegative(),
}).strict().superRefine((value, context) => {
  if (value.kind === "quick" && !meetupQuickMessages.includes(value.body as typeof meetupQuickMessages[number])) {
    context.addIssue({ code:"custom", message:"unknown quick message", path:["body"] });
  }
});

export const meetupReportSchema = z.object({
  messageId: z.string().uuid().optional(),
  subjectId: z.string().min(16).max(128).optional(),
  reason: z.enum(["harassment", "sexual", "fraud", "spam", "threat", "privacy", "no_show", "other"]),
  detail: z.string().trim().max(300).default(""),
  idempotencyKey: z.string().uuid(),
}).strict().refine((value) => Boolean(value.messageId || value.subjectId), "report target required");

export const meetupAppealSchema = z.object({
  appealReference: z.string().regex(/^HS-[A-Z0-9]{12}$/),
  reason: z.string().trim().min(2).max(300).default("이용 제한 재검토를 요청합니다."),
  idempotencyKey: z.string().uuid(),
}).strict();

export const meetupModerationActionSchema = z.object({
  status: z.enum(["reviewing", "resolved", "dismissed"]),
  resolutionNote: z.string().trim().min(2).max(300),
  sanctionSubjectId: z.string().min(16).max(128).optional(),
  sanction: z.enum(["temporary", "permanent"]).optional(),
  suspendedUntil: z.string().datetime().optional(),
  unsuspendSubjectId: z.string().min(16).max(128).optional(),
  retainEvidenceUntil: z.string().datetime().optional(),
}).strict().superRefine((value, context) => {
  if (Boolean(value.sanctionSubjectId) !== Boolean(value.sanction)) context.addIssue({ code:"custom", message:"sanction subject and kind must be paired" });
  if (value.sanction === "temporary" && !value.suspendedUntil) context.addIssue({ code:"custom", message:"temporary sanction requires expiry" });
  if (value.sanction === "permanent" && value.suspendedUntil) context.addIssue({ code:"custom", message:"permanent sanction cannot expire" });
  if (value.unsuspendSubjectId && value.sanctionSubjectId) context.addIssue({ code:"custom", message:"sanction and unsuspend are mutually exclusive" });
});

export type CreateMeetupInput = z.infer<typeof createMeetupSchema>;
export type MeetupStatus = z.infer<typeof meetupStatusSchema>;
export type MeetupParticipantStatus = z.infer<typeof meetupParticipantStatusSchema>;
export type MeetupBudget = z.infer<typeof meetupBudgetSchema>;
export type MeetupAlcoholStyle = z.infer<typeof meetupAlcoholStyleSchema>;
export type MeetupJoinMode = z.infer<typeof meetupJoinModeSchema>;

export type MeetupParticipant = {
  subjectId: string;
  publicAlias: string;
  status: MeetupParticipantStatus;
  joinedAt: string;
  checkedInAt: string | null;
};

export type MeetupMessage = {
  id: string;
  senderSubjectId: string;
  senderAlias: string;
  kind: "text" | "quick" | "system";
  body: string;
  sentAt: string;
};

export type MeetupSummary = CreateMeetupInput & {
  id: string;
  hostAlias: string;
  status: MeetupStatus;
  participantCount: number;
  createdAt: string;
  endedAt: string | null;
  chatClosesAt: string;
  revision: number;
};

export type MeetupDetail = MeetupSummary & {
  participants: MeetupParticipant[];
  currentSubjectId: string | null;
  currentSubjectStatus: MeetupParticipantStatus | null;
  canJoin: boolean;
  canChat: boolean;
  canModerate: boolean;
  checkInCode?: string;
};
